/**
 * Async autocomplete. Wraps an existing <input> element, adds a popup below
 * it with results fetched on each keystroke (debounced), and calls onSelect
 * when the user picks one.
 *
 * Usage:
 *   const ctl = mountAutocomplete(inputEl, {
 *     fetch: async (query, signal) => fetchResults(query, signal),
 *     renderItem: (item) => `<strong>${item.name}</strong>`,
 *     getItemLabel: (item) => item.name,
 *     onSelect: (item) => { ... },
 *     minLength: 1,
 *     debounceMs: 200,
 *     emptyText: 'Нічого не знайдено',
 *     hint: 'Почніть вводити, щоб шукати',
 *   });
 *   ctl.setValue('Київ');      // imperative value set without triggering search
 *   ctl.destroy();             // remove popup + listeners
 *
 * The trigger input must be inside a `position: relative` parent so the
 * popup positions correctly underneath it.
 */

let openCtl = null;

function closeOpen() {
  if (!openCtl) return;
  openCtl._closePopup();
}

/** @param {HTMLInputElement} input */
export function mountAutocomplete(input, opts) {
  const {
    fetch: fetchFn,
    renderItem,
    getItemLabel,
    onSelect,
    minLength = 1,
    debounceMs = 200,
    emptyText = 'Нічого не знайдено',
    hint = 'Почніть вводити для пошуку',
    loadingText = 'Завантаження…',
  } = opts;

  const parent = input.parentElement;
  if (parent && getComputedStyle(parent).position === 'static') {
    parent.style.position = 'relative';
  }

  const popup = document.createElement('div');
  popup.className = 'ac-popup';
  popup.style.display = 'none';
  popup.setAttribute('role', 'listbox');
  parent.appendChild(popup);

  let items = [];
  let activeIdx = -1;
  let abortCtl = null;
  let debounceTimer = null;
  let lastQuery = '';

  function renderPopup(state) {
    popup.classList.toggle('ac-loading', state === 'loading');
    if (state === 'hint')    { popup.innerHTML = `<div class="ac-hint">${hint}</div>`; return; }
    if (state === 'loading') { popup.innerHTML = `<div class="ac-hint">${loadingText}</div>`; return; }
    if (state === 'empty')   { popup.innerHTML = `<div class="ac-hint">${emptyText}</div>`; return; }
    popup.innerHTML = `<ul class="ac-list">${items.map((it, i) => `
      <li class="ac-item${i === activeIdx ? ' is-active' : ''}" data-i="${i}" role="option">${renderItem(it)}</li>
    `).join('')}</ul>`;
  }

  function openPopup() {
    if (openCtl && openCtl !== ctl) closeOpen();
    popup.style.display = '';
    openCtl = ctl;
  }
  function _closePopup() {
    popup.style.display = 'none';
    activeIdx = -1;
    if (openCtl === ctl) openCtl = null;
  }

  async function runQuery(q) {
    abortCtl?.abort();
    abortCtl = new AbortController();
    lastQuery = q;
    if (q.length < minLength) {
      items = [];
      renderPopup('hint');
      openPopup();
      return;
    }
    renderPopup('loading');
    openPopup();
    try {
      const results = await fetchFn(q, abortCtl.signal);
      // Ignore stale responses if the user typed again.
      if (q !== lastQuery) return;
      items = Array.isArray(results) ? results : [];
      activeIdx = items.length > 0 ? 0 : -1;
      renderPopup(items.length === 0 ? 'empty' : 'items');
    } catch (err) {
      if (err.name === 'AbortError') return;
      items = [];
      // The server side translates carrier API errors into Ukrainian, but
      // network-level failures (offline, CORS, server down) come through
      // as English "Failed to fetch" / "HTTP 502". Detect those by their
      // leading character and swap for a generic UA fallback so the
      // customer always sees something they can read.
      const raw = String(err.message || err).trim();
      const looksUkrainian = /^[А-ЯҐЄІЇа-яґєії]/.test(raw);
      const shown = looksUkrainian
        ? raw
        : 'Не вдалося завантажити дані. Спробуйте ще раз.';
      popup.innerHTML = `<div class="ac-hint ac-error">Помилка: ${shown.slice(0, 80)}</div>`;
      openPopup();
    }
  }

  function pick(i) {
    const it = items[i];
    if (!it) return;
    input.value = getItemLabel(it);
    _closePopup();
    onSelect?.(it);
  }

  function setActive(i) {
    if (items.length === 0) return;
    activeIdx = Math.max(0, Math.min(i, items.length - 1));
    popup.querySelectorAll('.ac-item').forEach((el, idx) => {
      el.classList.toggle('is-active', idx === activeIdx);
      if (idx === activeIdx) el.scrollIntoView({ block: 'nearest' });
    });
  }

  const onInput = () => {
    clearTimeout(debounceTimer);
    const q = input.value.trim();
    debounceTimer = setTimeout(() => runQuery(q), debounceMs);
  };
  const onFocus = () => {
    const q = input.value.trim();
    if (q.length >= minLength) runQuery(q);
    else { renderPopup('hint'); openPopup(); }
  };
  const onBlur = () => {
    // Delay close so a click on a list item still registers.
    setTimeout(() => { if (!popup.matches(':hover')) _closePopup(); }, 120);
  };
  const onKey = (e) => {
    if (popup.style.display === 'none') return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIdx + 1); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(activeIdx - 1); }
    else if (e.key === 'Enter')     { if (activeIdx >= 0) { e.preventDefault(); pick(activeIdx); } }
    else if (e.key === 'Escape')    { e.preventDefault(); _closePopup(); }
  };
  const onPopupMouseDown = (e) => {
    // mousedown (before blur) so we can pick before the input loses focus.
    const li = e.target.closest('.ac-item');
    if (!li) return;
    e.preventDefault();
    pick(Number(li.dataset.i));
  };

  input.addEventListener('input', onInput);
  input.addEventListener('focus', onFocus);
  input.addEventListener('blur', onBlur);
  input.addEventListener('keydown', onKey);
  popup.addEventListener('mousedown', onPopupMouseDown);

  const ctl = {
    /** Set the input value without firing a query (e.g. when restoring state). */
    setValue(v) { input.value = v || ''; },
    /** Force a popup refresh (e.g. when an upstream dependency changes). */
    refresh() { runQuery(input.value.trim()); },
    /** Tear down listeners + remove popup. */
    destroy() {
      input.removeEventListener('input', onInput);
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('blur', onBlur);
      input.removeEventListener('keydown', onKey);
      popup.removeEventListener('mousedown', onPopupMouseDown);
      abortCtl?.abort();
      clearTimeout(debounceTimer);
      popup.remove();
      if (openCtl === ctl) openCtl = null;
    },
    _closePopup,
  };
  return ctl;
}
